/**
 * JsonTreeView — minimal recursive JSON renderer for the Parsed Feedback
 * fallback. Used when a prompt type has no registered structured view
 * (concept_lab, learning_moments, textchat, voice, …) but the run still
 * produced parseable JSON we can give admins a useful read of.
 *
 * Objects and arrays are rendered as collapsible `<details>` elements
 * with their entry count surfaced in the summary. Primitives render
 * inline with type-specific styling (string/number/boolean/null) so
 * the eye can pick out shape at a glance.
 *
 * Not a feature-complete JSON viewer — no copy buttons, no editing,
 * no path strings. Lives here (not shared-ui) until a second consumer
 * shows up; promote then.
 */
import { useState } from 'react';
import './JsonTreeView.css';

type Json = unknown;

/** Best-effort parse of an LLM raw response. Strips a single leading
 *  ```json ... ``` markdown fence if present (some prompts wrap output)
 *  and tries `JSON.parse`. Returns `{ ok: true, value }` on success and
 *  `{ ok: false }` when the response isn't JSON — caller falls back to
 *  rendering the raw text. */
export function tryParseJson(raw: string): { ok: true; value: unknown } | { ok: false } {
    let s = raw.trim();
    if (s.startsWith('```json')) s = s.slice(7).trim();
    else if (s.startsWith('```')) s = s.slice(3).trim();
    if (s.endsWith('```')) s = s.slice(0, -3).trim();
    if (!s) return { ok: false };
    try {
        return { ok: true, value: JSON.parse(s) };
    } catch {
        return { ok: false };
    }
}

interface JsonTreeViewProps {
    value: Json;
    /** Initial open-state for top-level objects/arrays. Default: open. */
    defaultOpen?: boolean;
}

export function JsonTreeView({ value, defaultOpen = true }: JsonTreeViewProps) {
    return (
        <div className="json-tree">
            <JsonNode value={value} depth={0} defaultOpen={defaultOpen} />
        </div>
    );
}

interface JsonNodeProps {
    value: Json;
    depth: number;
    defaultOpen: boolean;
    /** When this node is a value within a parent object/array, the key
     *  rendered to its left (e.g. `"foo": …`). Top-level node has no key. */
    keyLabel?: string;
}

function JsonNode({ value, depth, defaultOpen, keyLabel }: JsonNodeProps) {
    if (value === null) return <PrimitiveLine keyLabel={keyLabel} className="jt-null">null</PrimitiveLine>;
    if (value === undefined) return <PrimitiveLine keyLabel={keyLabel} className="jt-null">undefined</PrimitiveLine>;
    if (typeof value === 'string') return <PrimitiveLine keyLabel={keyLabel} className="jt-string">"{value}"</PrimitiveLine>;
    if (typeof value === 'number') return <PrimitiveLine keyLabel={keyLabel} className="jt-number">{String(value)}</PrimitiveLine>;
    if (typeof value === 'boolean') return <PrimitiveLine keyLabel={keyLabel} className="jt-bool">{String(value)}</PrimitiveLine>;
    if (Array.isArray(value)) return <ContainerNode value={value} depth={depth} defaultOpen={defaultOpen} keyLabel={keyLabel} kind="array" />;
    if (typeof value === 'object') return <ContainerNode value={value as Record<string, Json>} depth={depth} defaultOpen={defaultOpen} keyLabel={keyLabel} kind="object" />;
    // Fallback for exotic types (function, symbol, etc.) — should not occur for parsed JSON.
    return <PrimitiveLine keyLabel={keyLabel} className="jt-unknown">{String(value)}</PrimitiveLine>;
}

function PrimitiveLine({ keyLabel, className, children }: { keyLabel?: string; className: string; children: React.ReactNode }) {
    return (
        <div className="jt-row">
            {keyLabel !== undefined && <span className="jt-key">"{keyLabel}":</span>}
            <span className={className}>{children}</span>
        </div>
    );
}

function ContainerNode({
    value, depth, defaultOpen, keyLabel, kind,
}: {
    value: Record<string, Json> | Json[];
    depth: number;
    defaultOpen: boolean;
    keyLabel?: string;
    kind: 'object' | 'array';
}) {
    // First two levels open by default, deeper start collapsed so a deeply
    // nested payload doesn't explode visually.
    const initialOpen = defaultOpen && depth < 2;
    const [open, setOpen] = useState(initialOpen);

    const entries = kind === 'array'
        ? (value as Json[]).map((v, i) => [String(i), v] as const)
        : Object.entries(value as Record<string, Json>);

    const openBracket = kind === 'array' ? '[' : '{';
    const closeBracket = kind === 'array' ? ']' : '}';
    const summary = entries.length === 0
        ? `${openBracket}${closeBracket}`
        : `${openBracket} ${entries.length} ${entries.length === 1 ? 'item' : 'items'} ${closeBracket}`;

    return (
        <div className="jt-row jt-container">
            <button
                type="button"
                className="jt-toggle"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            >
                <span className="jt-chevron">{open ? '▾' : '▸'}</span>
                {keyLabel !== undefined && <span className="jt-key">"{keyLabel}":</span>}
                <span className="jt-summary">{summary}</span>
            </button>
            {open && entries.length > 0 && (
                <div className="jt-children">
                    {entries.map(([k, v]) => (
                        <JsonNode
                            key={k}
                            value={v}
                            depth={depth + 1}
                            defaultOpen={defaultOpen}
                            keyLabel={kind === 'object' ? k : undefined}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
